/**
 * The public document link's API client - what a QR code on a printed invoice
 * or offer leads to.
 *
 * No session is involved. The token in the address is the whole credential, so
 * every call carries it in the path and nothing here reads or needs `me`. A
 * miss is always a plain 404, which is the same answer a revoked link and a
 * draft document give.
 */
import { api } from "./api";
import type { EstimateAnswerState, EstimateDecision } from "@wa/core/estimate-answer";

export interface PublicDocLine {
  description: string;
  details: string | null;
  qty: number;
  unit: string | null;
  unitPriceCents: number;
  taxRatePct: number;
  discountPct: number | null;
  netCents: number;
}

export interface PublicDocSeller {
  name: string;
  regCode: string;
  vatNumber: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  iban: string;
  bankName: string;
}

/**
 * The recipient, as the document's header prints them. Mirrors the seller
 * block: every field but the name is nullable, and the page drops the empty
 * ones rather than printing blank lines.
 */
export interface PublicDocBuyer {
  name: string;
  regCode: string | null;
  vatNumber: string | null;
  email: string | null;
  /** Street, postcode and town, country - already joined by the server. */
  address: string;
}

export interface PublicDocComment {
  id: string;
  authorKind: string;
  authorName: string;
  body: string;
  createdAt: string;
}

export interface PublicDocAnswer {
  state: EstimateAnswerState;
  answerable: boolean;
  decision: { decision: string; name: string; at: string; note?: string } | null;
}

export interface PublicDoc {
  type: "invoice" | "estimate" | "proposal";
  number: string;
  title: string | null;
  status: string;
  currency: string;
  issueDate: string | null;
  dueDate: string | null;
  validUntil: string | null;
  qtyMode: string;
  lines: PublicDocLine[];
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  /**
   * Whether the VAT column and the VAT row belong on this document. The
   * server's answer, not the browser's: only the server knows whether the
   * seller charges VAT today, and the page has to print the same document the
   * client already holds as a PDF.
   *
   * Optional on the type because it is JSON off the wire and a cached bundle
   * can outlive the API it was built against. Absent means yes, which is what
   * every response written before the switch existed meant.
   */
  showVat?: boolean;
  totalCents: number;
  paidCents: number;
  dueCents: number;
  notes: string | null;
  terms: string | null;
  seller: PublicDocSeller;
  clientName: string;
  /**
   * The recipient block. Optional for the reason `showVat` is: it is JSON off
   * the wire and a cached bundle can outlive the API it was built against.
   * Absent means the page draws `clientName` on its own, which is all any
   * response written before this block existed could offer.
   */
  buyer?: PublicDocBuyer;
  answer: PublicDocAnswer | null;
  /** The SEPA payload behind the on-screen pay code. Invoices only. */
  payEpc: string | null;
  /**
   * Whether the desk still offers a bank transfer for this invoice.
   *
   * Optional for the reason `showVat` above is: it is JSON off the wire and a
   * cached bundle can outlive the API it was built against. Absent means yes,
   * which is what every response written before the switch existed meant.
   *
   * NOT the same question as "is there an IBAN". A desk with no IBAN typed in
   * still shows the payee, the amount and the reference, and always has.
   */
  payTransfer?: boolean;
  /**
   * The language the DOCUMENT is written in - not the reader's preference.
   *
   * The page opens in it, so a client sent a German invoice reads a German page
   * whichever browser they follow the link in, and the page and the PDF behind
   * it say the same words. Optional for the same reason `payTransfer` is: a
   * cached bundle can outlive the API, and absent has to keep meaning what it
   * always did, which is "leave the interface language alone".
   */
  locale?: string;
  comments: PublicDocComment[];
}

export interface PublicDocResponse {
  doc: PublicDoc;
  /** Where signing in leads, for the "more of your records" link. */
  portalUrl: string;
}

export const publicDoc = {
  read: (token: string) => api.get<PublicDocResponse>(`/public/sales/${encodeURIComponent(token)}`),
  answer: (token: string, body: { decision: EstimateDecision; name: string; email?: string; note?: string }) =>
    api.post<PublicDocResponse>(`/public/sales/${encodeURIComponent(token)}/decision`, body),
  comment: (token: string, body: { name: string; email?: string; body: string }) =>
    api.post<{ comment: PublicDocComment }>(`/public/sales/${encodeURIComponent(token)}/comments`, body),
};

/** The PDF opens as a plain link, the way the portal's own PDFs do. */
export function publicPdfHref(token: string): string {
  return `/api/public/sales/${encodeURIComponent(token)}/pdf`;
}
